<template>
  <div>
    <Spinner
      v-if="isLoadingPolicies || isSavingPolicies"
      color="primary"
      size="5rem"
      width="0.5rem"
      class="absolute inset-0 flex justify-center items-center bg-white bg-opacity-50 z-50"
    />

    <div
      class="flex flex-col md:flex-row justify-between gap-4 text-sm text-gray-600 mb-4 px-2 mt-4"
    >
      <div class="flex flex-wrap gap-4">
        <div class="flex items-center gap-2">
          <span
            class="w-2.5 h-3.5 rounded-full border border-green-500 bg-green-100 inline-block"
          ></span>
          Vinculada
        </div>
        <div class="flex items-center gap-2">
          <span
            class="w-2.5 h-3.5 rounded-full border border-yellow-400 bg-yellow-100 inline-block"
          ></span>
          Em edição
        </div>
        <div class="flex items-center gap-2">
          <span
            class="w-2.5 h-3.5 rounded-full border border-red-500 bg-red-100 inline-block"
          ></span>
          Não vinculada
        </div>
      </div>

      <div class="flex flex-col sm:flex-row items-start sm:items-center gap-2 relative">
        <button
          v-if="hasChanges"
          data-testid="revert-policy-changes-button"
          class="text-sm text-gray-400 hover:text-gray-700"
          @click="handleRevertChanges"
        >
          Reverter alterações
        </button>

        <outline-button
          data-testid="add-policy-button"
          text="Adicionar política"
          height="32px"
          :append-icon="mdiPlus"
          class="text-sm !w-auto !px-4 !py-1"
          @click="showAddPolicyForm = !showAddPolicyForm"
          :disabled="!canEdit"
        />

        <div
          v-if="showAddPolicyForm"
          class="fixed top-[calc(25vh+4rem)] right-[25vw] z-[50] bg-white border border-gray-200 rounded-lg shadow-md p-4 w-[320px]"
        >
          <div class="flex justify-between items-center mb-3">
            <h3 class="text-base font-semibold text-gray-800">Adicionar política</h3>
            <button
              data-testid="cancel-add-policy-button"
              class="text-gray-400 hover:text-gray-700"
              @click="handleCancelAddPolicy"
            >
              ✕
            </button>
          </div>

          <ds-policy-select
            v-model="selectedPolicy"
            data-testid="add-policy-select-input"
            :company-id="props.companyId"
            :fetch-policies="props.adapter?.fetchPolicyOptions"
            class="w-full mb-4"
          />

          <DatePicker
            v-model="startDate"
            locale="pt-BR"
            :masks="{ modelValue: 'YYYY-MM-DD', input: 'DD/MM/YYYY' }"
            is-required
          >
            <template #default="{ inputValue, inputEvents }">
              <v-text-field
                label="Início da vigência"
                data-testid="add-policy-start-date-input"
                :model-value="inputValue"
                v-on="inputEvents"
                class="w-full"
                color="primary20"
                variant="outlined"
                density="comfortable"
                :prepend-inner-icon="mdiCalendar"
              />
            </template>
          </DatePicker>

          <DatePicker
            v-model="endDate"
            locale="pt-BR"
            :masks="{ modelValue: 'YYYY-MM-DD', input: 'DD/MM/YYYY' }"
            is-required
          >
            <template #default="{ inputValue, inputEvents }">
              <v-text-field
                label="Final da vigência"
                data-testid="add-policy-end-date-input"
                :model-value="inputValue"
                v-on="inputEvents"
                class="w-full mb-4"
                color="primary20"
                variant="outlined"
                density="comfortable"
                :prepend-inner-icon="mdiCalendar"
              />
            </template>
          </DatePicker>

          <filled-button
            data-testid="confirm-add-policy-button"
            text="Incluir política"
            width="100%"
            @click="handleAddPolicy"
          />
        </div>

        <div
          v-if="showEditPolicyForm"
          class="fixed top-[calc(25vh+4rem)] right-[25vw] z-50 bg-white border border-gray-200 rounded-lg shadow-md p-4 w-[320px]"
        >
          <div class="flex justify-between items-center mb-3">
            <h3 class="text-base font-semibold text-gray-800">Editar política</h3>
            <button
              data-testid="cancel-edit-policy-button"
              class="text-gray-400 hover:text-gray-700"
              @click="handleCancelEditPolicy"
            >
              ✕
            </button>
          </div>

          <div class="text-xs text-gray-500 font-medium mb-3">
            Data de criação: {{ formatToBR(editPolicy?.linkedAt || '') }}
          </div>

          <ds-policy-select
            v-model="editPolicy!.policyName"
            :fetch-policies="props.adapter?.fetchPolicyOptions"
            class="w-full mb-4"
            disabled
          />

          <DatePicker
            v-model="editPolicy!.startDate"
            locale="pt-BR"
            :masks="{ modelValue: 'YYYY-MM-DD', input: 'DD/MM/YYYY' }"
            is-required
          >
            <template #default="{ inputValue, inputEvents }">
              <v-text-field
                label="Início da vigência"
                data-testid="edit-policy-start-date-input"
                :model-value="inputValue"
                v-on="inputEvents"
                class="w-full"
                color="primary20"
                variant="outlined"
                density="comfortable"
                :prepend-inner-icon="mdiCalendar"
              />
            </template>
          </DatePicker>

          <DatePicker
            v-model="editPolicy!.endDate"
            locale="pt-BR"
            :masks="{ modelValue: 'YYYY-MM-DD', input: 'DD/MM/YYYY' }"
            is-required
          >
            <template #default="{ inputValue, inputEvents }">
              <v-text-field
                label="Final da vigência"
                data-testid="edit-policy-end-date-input"
                :model-value="inputValue"
                v-on="inputEvents"
                class="w-full mb-4"
                color="primary20"
                variant="outlined"
                density="comfortable"
                :prepend-inner-icon="mdiCalendar"
              />
            </template>
          </DatePicker>

          <div class="flex gap-2">
            <filled-button
              data-testid="confirm-edit-policy-button"
              text="Concluir"
              width="50%"
              @click="handleConfirmEditPolicy"
            />
            <filled-button
              data-testid="delete-policy-link-button"
              text="Excluir vínculo"
              width="50%"
              class="!bg-transparent border-2 !border-red-500 !text-red-500"
              @click="handleDeletePolicy"
            />
          </div>
        </div>
      </div>
    </div>

    <TableModular v-if="policies.length" :rows="policies" :columns="tableColumns">
      <template #actions="{ index }">
        <EditIcon
          v-if="canEdit"
          data-testid="edit-policy-link-button"
          color="#2f7f91"
          class="cursor-pointer ml-4 w-5 h-5"
          @click="handleEdit(index)"
        />
      </template>
    </TableModular>

    <EmptyList v-else title="Nenhuma política vinculada encontrada" class="mt-8" />

    <ConfirmDeleteAction
      data-testid="confirm-empty-save-dialog"
      :show="showEmptyListConfirm"
      title="Remover todos os vínculos?"
      description="Você removeu todas as políticas da lista. Ao salvar, todos os vínculos de política deste recurso serão excluídos permanentemente. Deseja continuar?"
      text-button="Excluir vínculos"
      @close-modal="showEmptyListConfirm = false"
      @handle-confirm="handleConfirmEmptySave"
    />
  </div>
</template>

<script lang="ts" setup>
import { ref, computed, watch, onMounted } from 'vue'
import { useToast } from 'vue-toastification'
import { useErrorToast } from '@/composables/useErrorToast'
import { mdiCalendar, mdiPlus } from '@mdi/js'
import { DatePicker } from 'v-calendar'

import PolicyService from '@/Network/Services/DsPolicyService'
import type {
  CompanyDomainItem,
  GetCompanyDomainResponse,
  GetHealthUnitDomainResponse,
  GetPositionDomainResponse,
  GetScheduleDomainResponse
} from '@/Network/Types/Responses/DsPolicyResponses'
import TableModular from '@/components/designSystem/Table/TableModular/TableModular.vue'
import OutlineButton from '@/components/designSystem/Buttons/OutlineButton/index.vue'
import FilledButton from '@/components/designSystem/Buttons/FilledButton/index.vue'
import EmptyList from '@/components/EmptyList.vue'
import ConfirmDeleteAction from '@/components/designSystem/modals/confirm.delete.action.vue'
import Spinner from '@/components/others/Spinner.vue'
import EditIcon from '@/assets/icons/edit.vue'
import { usePolicyLinkEditPermission } from '@/hooks/usePolicyLinkPermissions'
import { AuthorizationResourcesAndSubjects } from '@/types/authorization'

import DsPolicySelect from '../Select/DsPolicySelect.vue'
import type { PolicyLinksAdapter } from './types'

interface DraftItem {
  policyId: number
  policyName: string
  startDate: string
  endDate: string
  linkedAt: string
  linkedBy: string
  status: 'vinculada' | 'em_edicao' | 'nao_vinculada'
}

type PolicyLinkDomainItem = Pick<
  CompanyDomainItem,
  'startDate' | 'endDate' | 'dsPolicy' | 'createdAt' | 'linkedBy'
>

interface SelectedDsPolicy {
  id: number
  name: string
  createdBy?: string
}

const props = defineProps<{
  resourceType: AuthorizationResourcesAndSubjects
  resourceId: number
  companyId?: number
  timezone?: string
  adapter?: PolicyLinksAdapter
  // Ignora o gate de permissão de edição (usePolicyLinkEditPermission).
  // Usado pela tab de qualificação para repasse, que não tem permissão própria.
  bypassEditPermission?: boolean
}>()

const emit = defineEmits<{
  'update:isSaving': [value: boolean]
  'update:hasUnsavedChanges': [value: boolean]
  saved: []
}>()

const toast = useToast()
const { notifyError } = useErrorToast()

const policyService = new PolicyService()

const draftItems = ref<DraftItem[]>([])
const showAddPolicyForm = ref(false)
const selectedPolicy = ref<SelectedDsPolicy | undefined>(undefined)
const startDate = ref<string | Date>('')
const endDate = ref<string | Date>('')
const showEditPolicyForm = ref(false)
const editItemIndex = ref<number | null>(null)
const editPolicy = ref<DraftItem | null>(null)
const originalItems = ref<DraftItem[]>([])
const isLoadingPolicies = ref(false)
const isSavingPolicies = ref(false)
const showEmptyListConfirm = ref(false)

const { canEditPolicyLinks } = usePolicyLinkEditPermission(() => props.resourceType)

const canEdit = computed(() => props.bypassEditPermission || canEditPolicyLinks.value)

const tableColumns = computed(() => [
  {
    key: 'name',
    label: props.adapter?.policyColumnLabel ?? 'Política de restrição',
    cols: 'lg:col-span-3 md:col-span-4 col-span-12'
  },
  { key: 'period', label: 'Período', cols: 'lg:col-span-3 md:col-span-4 col-span-12' },
  {
    key: 'linkedAt',
    label: 'Data do vínculo',
    cols: 'lg:col-span-1 md:col-span-3 col-span-12'
  },
  {
    key: 'responsible',
    label: 'Responsável',
    cols: 'lg:col-span-2 md:col-span-4 col-span-12'
  }
])

const fetchLinkedPolicies = async () => {
  isLoadingPolicies.value = true
  try {
    if (props.adapter) {
      const items = await props.adapter.listLinks(props.resourceType, props.resourceId)
      draftItems.value = items.map((item) => ({ ...item, status: 'vinculada' as const }))
      originalItems.value = JSON.parse(JSON.stringify(draftItems.value))
      return
    }

    let response:
      | GetCompanyDomainResponse
      | GetHealthUnitDomainResponse
      | GetScheduleDomainResponse
      | GetPositionDomainResponse
      | undefined
    const { resourceType, resourceId, timezone } = props

    switch (resourceType) {
      case AuthorizationResourcesAndSubjects.Company:
        response = await policyService.listCompanyDsPolicyLinks(resourceId)
        break
      case AuthorizationResourcesAndSubjects.HealthUnit:
        response = await policyService.listHealthUnitDsPolicyLinks(resourceId)
        break
      case AuthorizationResourcesAndSubjects.Schedule:
        response = await policyService.listScheduleDsPolicyLinks(resourceId)
        break
      case AuthorizationResourcesAndSubjects.Position:
        response = await policyService.listPositionDsPolicyLinks(resourceId)
        break
    }

    const items: PolicyLinkDomainItem[] = response?.data ?? []
    draftItems.value = items.map((item) => {
      const start = item.startDate
      const end = item.endDate

      const isSchedule = resourceType === AuthorizationResourcesAndSubjects.Schedule

      const adjustedStart = isSchedule && timezone ? applyTimezoneOffset(start, timezone) : start

      const adjustedEnd = isSchedule && timezone ? applyTimezoneOffset(end, timezone, true) : end

      return {
        policyId: item.dsPolicy?.id,
        policyName: item.dsPolicy?.name,
        startDate: adjustedStart,
        endDate: adjustedEnd,
        linkedAt: item.createdAt,
        linkedBy: item.linkedBy?.name ?? '',
        status: 'vinculada'
      }
    })

    originalItems.value = JSON.parse(JSON.stringify(draftItems.value))
  } catch (error: unknown) {
    notifyError(error, 'Não foi possível carregar as políticas vinculadas.')
  } finally {
    isLoadingPolicies.value = false
  }
}

onMounted(() => {
  fetchLinkedPolicies()
})

const applyTimezoneOffset = (dateStr: string, offset: string, isEnd = false): string => {
  const date = new Date(dateStr)

  if (isEnd) {
    date.setUTCDate(date.getUTCDate() - 1)
    date.setUTCHours(23, 59, 59, 999)
  } else {
    date.setUTCHours(0, 0, 0, 0)
  }

  const [sign, hours, minutes] = offset.match(/([+-])(\d{2}):(\d{2})/)!.slice(1)
  const totalOffset = (parseInt(hours) * 60 + parseInt(minutes)) * (sign === '+' ? -1 : 1)
  const adjustedDate = new Date(date.getTime() + totalOffset * 60 * 1000)
  return adjustedDate.toISOString()
}

const hasChanges = computed(() => {
  return JSON.stringify(draftItems.value) !== JSON.stringify(originalItems.value)
})

watch(hasChanges, (value) => {
  emit('update:hasUnsavedChanges', value)
})

watch(isSavingPolicies, (value) => {
  emit('update:isSaving', value)
})

const handleRevertChanges = () => {
  draftItems.value = JSON.parse(JSON.stringify(originalItems.value))
}

const handleCancelAddPolicy = () => {
  showAddPolicyForm.value = false
  selectedPolicy.value = undefined
  startDate.value = ''
  endDate.value = ''
}

const formatDate = (date: string | Date, isStart = true): string => {
  if (typeof date === 'string') date = new Date(date)
  const adjusted = new Date(date)
  adjusted.setUTCHours(isStart ? 0 : 23, isStart ? 0 : 59, isStart ? 0 : 59, isStart ? 0 : 999)
  return adjusted.toISOString()
}

const handleAddPolicy = () => {
  if (!selectedPolicy.value || !startDate.value || !endDate.value) {
    toast.error('Preencha todos os campos para incluir a política.')
    return
  }

  draftItems.value.push({
    policyId: selectedPolicy.value.id,
    policyName: selectedPolicy.value.name,
    startDate: formatDate(startDate.value, true),
    endDate: formatDate(endDate.value, false),
    linkedAt: new Date().toISOString(),
    linkedBy: selectedPolicy.value.createdBy ?? 'Usuário atual',
    status: 'nao_vinculada'
  })

  handleCancelAddPolicy()
}

const handleSave = async () => {
  const invalidItems = draftItems.value.filter(
    (item) => !item.policyId || !item.startDate || !item.endDate
  )

  if (invalidItems.length > 0) {
    // Vínculo com vigência aberta (endDate null vindo do backend) exige data final
    const onlyOpenEnded = invalidItems.every(
      (item) => item.policyId && item.startDate && !item.endDate
    )
    toast.error(
      onlyOpenEnded
        ? 'Há vínculo com vigência aberta (sem data final). Preencha a data final para salvar.'
        : 'Todos os campos devem estar preenchidos.'
    )
    return
  }

  // O PATCH substitui a timeline inteira: lista vazia apaga todos os vínculos
  if (draftItems.value.length === 0 && originalItems.value.length > 0) {
    showEmptyListConfirm.value = true
    return
  }

  await performSave()
}

const handleConfirmEmptySave = async () => {
  showEmptyListConfirm.value = false
  await performSave()
}

const performSave = async () => {
  isSavingPolicies.value = true
  try {
    const { resourceType, resourceId } = props

    if (props.adapter) {
      await props.adapter.saveLinks(
        resourceType,
        resourceId,
        draftItems.value.map((item) => ({
          policyId: Number(item.policyId),
          startDate: formatDate(item.startDate, true),
          endDate: formatDate(item.endDate, false)
        }))
      )

      toast.success('Políticas salvas com sucesso!')
      await fetchLinkedPolicies()
      emit('saved')
      return
    }

    const formattedItems = draftItems.value.map((item) => ({
      dsPolicyId: Number(item.policyId),
      startDate: formatDate(item.startDate, true),
      endDate: formatDate(item.endDate, false)
    }))

    switch (resourceType) {
      case AuthorizationResourcesAndSubjects.Company:
        await policyService.updateCompanyDsPolicyLinks(resourceId, {
          policies: formattedItems
        })
        break
      case AuthorizationResourcesAndSubjects.HealthUnit:
        await policyService.updateHealthUnitDsPolicyLinks(resourceId, {
          policies: formattedItems
        })
        break
      case AuthorizationResourcesAndSubjects.Schedule:
        await policyService.updateScheduleDsPolicyLinks(resourceId, {
          policies: formattedItems
        })
        break
      case AuthorizationResourcesAndSubjects.Position:
        await policyService.updatePositionDsPolicyLinks(resourceId, {
          policies: formattedItems
        })
        break
      default:
        toast.error('Tipo de recurso não reconhecido.')
        return
    }

    toast.success('Políticas salvas com sucesso!')
    await fetchLinkedPolicies()
    emit('saved')
  } catch (error: unknown) {
    notifyError(error, 'Não foi possível salvar as políticas vinculadas.')
  } finally {
    isSavingPolicies.value = false
  }
}

const handleEdit = (index: number) => {
  editItemIndex.value = index
  editPolicy.value = { ...draftItems.value[index] }
  showEditPolicyForm.value = true
}

const handleCancelEditPolicy = () => {
  showEditPolicyForm.value = false
  editItemIndex.value = null
  editPolicy.value = null
}

const handleConfirmEditPolicy = () => {
  if (!editPolicy.value) {
    toast.error('Algo deu errado. Dados da edição não encontrados.')
    return
  }

  if (!editPolicy.value.startDate || !editPolicy.value.endDate) {
    toast.error('Preencha todos os campos.')
    return
  }

  editPolicy.value.startDate = formatDate(editPolicy.value.startDate, true)
  editPolicy.value.endDate = formatDate(editPolicy.value.endDate, false)

  if (editItemIndex.value !== null) {
    draftItems.value[editItemIndex.value] = {
      ...editPolicy.value,
      status: 'em_edicao'
    }

    handleCancelEditPolicy()
  }
}

const handleDeletePolicy = () => {
  if (editItemIndex.value !== null) {
    draftItems.value.splice(editItemIndex.value, 1)
    handleCancelEditPolicy()
  }
}

const formatToBR = (isoDate: string) => {
  if (!isoDate) return '-'

  const date = new Date(isoDate)
  const day = String(date.getUTCDate()).padStart(2, '0')
  const month = String(date.getUTCMonth() + 1).padStart(2, '0')
  const year = date.getUTCFullYear()

  return `${day}/${month}/${year}`
}

const policies = computed(() =>
  draftItems.value.map((item) => ({
    id: item.policyId,
    name: item.policyName,
    period:
      item.startDate && item.endDate
        ? `${formatToBR(item.startDate)} à ${formatToBR(item.endDate)}`
        : '-',
    linkedAt: item.linkedAt ? formatToBR(item.linkedAt) : '-',
    responsible: item.linkedBy ?? '-',
    status: item.status
  }))
)

defineExpose({ handleSave, handleRevertChanges })
</script>
